import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardHeader } from '@/components/common';
import { policyService } from '@/services/policies';
import { renewalService } from '@/services/renewals';
import { formatCurrency, formatDate } from '@/utils';
import {
  EXPIRED_LINK_MESSAGE,
  NO_LINK_MESSAGE,
  REPORTED_MESSAGE,
  RENEWAL_LINK_EXPIRED,
  apiErrorCode,
} from './renewalPaymentState';

/**
 * #345 phase 1 -- Renew now on the web policy page, for the policy's customer.
 *
 * The web twin of the mini-app's `RenewNowCard`: the insurer generated the
 * payment link, the URL is fetched only on the tap (`POST .../pay/`) and opened
 * in a new tab; "I have paid" tells the advisor; the receipt appears once the
 * advisor confirms. Renders nothing for a policy that is not due and has no link.
 */
export function RenewNowCard({ policyId, insurerName }: { policyId: string; insurerName?: string | null }) {
  const { data: item, refetch } = useQuery({
    queryKey: ['renewal-queue-item', policyId],
    queryFn: () => renewalService.getRenewalQueueItem(policyId),
    enabled: !!policyId,
    retry: false,
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [leavingTo, setLeavingTo] = useState<string | null>(null);
  // #464: set only when the browser blocked the new tab, so the customer can still reach the page.
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [proof, setProof] = useState<File | null>(null);

  if (!item) return null;
  const block = item.renewal_payment;
  const state = block?.state ?? 'no_link';
  const insurer = insurerName || item.insurer_name || 'the insurer';
  const due =
    item.in_queue && !item.is_installment_policy && item.days_to_expiry <= 60 && item.days_to_expiry >= -30;

  if (!block || state === 'no_link') {
    if (!due) return null;
    return (
      <Card>
        <CardHeader title="Renew your policy" />
        <p className="text-sm text-gray-600 dark:text-gray-300">{NO_LINK_MESSAGE}</p>
      </Card>
    );
  }

  const renewNow = async () => {
    setBusy(true);
    setMessage(null);
    setBlockedUrl(null);
    try {
      const start = await renewalService.startRenewalPayment(policyId);
      setLeavingTo(start.host);
      // No "noopener" feature: it makes window.open return null even on success,
      // which would hide a popup block. The opener is cut by hand instead.
      const opened = window.open(start.redirect_url, '_blank');
      if (opened) {
        opened.opener = null;
      } else {
        setBlockedUrl(start.redirect_url);
      }
    } catch (error) {
      if (apiErrorCode(error) === RENEWAL_LINK_EXPIRED) {
        setMessage(EXPIRED_LINK_MESSAGE);
        void refetch();
      } else {
        setMessage('Could not open the payment page. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const reportPaid = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await renewalService.reportRenewalPaid(policyId, proof);
      setConfirming(false);
      setProof(null);
      void refetch();
    } catch {
      setMessage('Could not tell your advisor. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const downloadReceipt = async () => {
    const receipt = block.receipt;
    if (!receipt?.document_id) return;
    try {
      const blob = await policyService.downloadPolicyDocument(receipt.policy_id, receipt.document_id);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = receipt.document_name || `receipt-${receipt.number ?? 'renewal'}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 2000);
    } catch {
      setMessage('Could not download the receipt. Try again.');
    }
  };

  if (state === 'paid') {
    const receipt = block.receipt;
    return (
      <Card>
        <CardHeader title="Renewal payment received" />
        {receipt && (
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-2">
              <dt className="text-gray-500 dark:text-gray-400">Receipt</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">{receipt.number}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-gray-500 dark:text-gray-400">Paid on</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">{formatDate(receipt.paid_at)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-gray-500 dark:text-gray-400">Amount</dt>
              <dd className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(receipt.amount)}</dd>
            </div>
            {receipt.insurer_reference && (
              <div className="flex justify-between gap-2">
                <dt className="text-gray-500 dark:text-gray-400">Insurer reference</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100 break-all">{receipt.insurer_reference}</dd>
              </div>
            )}
          </dl>
        )}
        {receipt?.is_acknowledgement && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            This is your advisor&apos;s acknowledgement; the insurer&apos;s own receipt and renewed policy follow.
          </p>
        )}
        {receipt?.document_id && (
          <Button variant="outline" size="sm" className="mt-3 w-full justify-center" onClick={() => void downloadReceipt()}>
            Download receipt
          </Button>
        )}
        {message && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{message}</p>}
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Renew your policy"
        subtitle={item.days_to_expiry < 0 ? 'Your policy has expired; renew to restore cover.' : undefined}
      />
      <div className="space-y-3 text-sm">
        {block.amount && (
          <div>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(block.amount)}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              You will pay on {insurer}&apos;s secure payment page.
              {block.expires_at ? ` Link valid till ${formatDate(block.expires_at)}.` : ''}
            </p>
          </div>
        )}
        {state === 'reported_paid' && (
          <p className="rounded-md bg-amber-50 p-3 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            {REPORTED_MESSAGE}
          </p>
        )}
        {state === 'expired' && !block.can_pay ? (
          <p className="text-red-600 dark:text-red-400">{EXPIRED_LINK_MESSAGE}</p>
        ) : (
          <Button className="w-full justify-center" isLoading={busy && !confirming} onClick={() => void renewNow()}>
            Renew now
          </Button>
        )}
        {leavingTo && <p className="text-xs text-gray-500 dark:text-gray-400">You are leaving to {leavingTo} to pay.</p>}
        {blockedUrl && (
          <p className="text-xs text-amber-800 dark:text-amber-200">
            Your browser blocked the new window.{' '}
            <a href={blockedUrl} target="_blank" rel="noopener noreferrer" className="underline">
              Open the payment page
            </a>
          </p>
        )}
        {state === 'link_active' && !confirming && (
          <Button variant="outline" className="w-full justify-center" onClick={() => setConfirming(true)}>
            I have paid
          </Button>
        )}
        {confirming && (
          <div className="space-y-2 rounded-md bg-gray-50 p-3 dark:bg-slate-800">
            <p>We will tell your advisor, who will confirm the payment with {insurer}.</p>
            <label className="block text-xs text-gray-600 dark:text-gray-300" htmlFor={`web-proof-${policyId}`}>
              Attach receipt (optional)
            </label>
            <input
              id={`web-proof-${policyId}`}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setProof(e.target.files?.[0] ?? null)}
              className="block w-full text-xs"
            />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button size="sm" isLoading={busy} onClick={() => void reportPaid()}>
                Yes, I have paid
              </Button>
            </div>
          </div>
        )}
        {message && <p className="text-xs text-red-600 dark:text-red-400">{message}</p>}
      </div>
    </Card>
  );
}
